'use client';

import { createContext, useContext, useState, useCallback } from 'react';

interface CursorState {
  isHovering: boolean;
  text: string;
  scale: number;
  blur: boolean;
}

interface CursorContextType {
  cursorState: CursorState;
  setCursorHover: (isHovering: boolean) => void;
  setCursorText: (text: string) => void;
  setCursorScale: (scale: number) => void;
  setCursorBlur: (blur: boolean) => void;
  resetCursor: () => void;
}

const defaultState: CursorState = {
  isHovering: false,
  text: '',
  scale: 1,
  blur: false,
};

const CursorContext = createContext<CursorContextType | null>(null);

export function useCursor() {
  const context = useContext(CursorContext);
  if (!context) {
    throw new Error('useCursor must be used within CursorProvider');
  }
  return context;
}

export function CursorProvider({ children }: { children: React.ReactNode }) {
  const [cursorState, setCursorState] = useState<CursorState>(defaultState);

  const setCursorHover = useCallback((isHovering: boolean) => {
    setCursorState((prev) => ({ ...prev, isHovering }));
  }, []);

  const setCursorText = useCallback((text: string) => {
    setCursorState((prev) => ({ ...prev, text }));
  }, []);

  const setCursorScale = useCallback((scale: number) => {
    setCursorState((prev) => ({ ...prev, scale }));
  }, []);

  const setCursorBlur = useCallback((blur: boolean) => {
    setCursorState((prev) => ({ ...prev, blur }));
  }, []);

  const resetCursor = useCallback(() => {
    setCursorState(defaultState);
  }, []);

  return (
    <CursorContext.Provider
      value={{
        cursorState,
        setCursorHover,
        setCursorText,
        setCursorScale,
        setCursorBlur,
        resetCursor,
      }}
    >
      {children}
    </CursorContext.Provider>
  );
}
